import { Box, Dialog } from "@mui/material";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_EASE,
  SITE_HEADER_HEIGHT_PX,
  SITE_MEDIA,
  SITE_TYPE,
  SITE_VAR,
  SITE_WRAP,
} from "src/domains/shared/constants/styles/site";
import { LinkCard } from "../siteKit/LinkCard";
import { SiteFileImage } from "../siteKit/media/SiteFileImage";
import { SiteImage } from "../siteKit/media/SiteImage";
import type { SiteFileKey } from "../siteKit/media/siteMedia";
import { revealRef } from "../siteKit/motion/reveal";
import { SITE_MOTION_ATTR } from "../SiteShell/siteRoutes";
import { riseOrder, SITE_HERO_RISE_STYLES } from "../siteKit/siteHeroStyles";
import { SiteText, SiteTitle } from "../siteKit/SiteText";
import { SiteSection } from "../siteKit/SiteSection";
import { siteTelFor } from "../siteKit/siteTel";
import { truncateNumber } from "src/domains/shared/utils/truncateNumber";
import { SiteRoot } from "../SiteShell/SiteRoot";
import { LoungeFrame } from "./LoungeFrame";

/** DroneSquare 채널톡 라운지 — 문의 페이지 안에 상담 창으로 띄운다(channel-talk-messenger: 채널톡 상담은 이 페이지에서만) */
const CHANNEL_LOUNGE_URL = "https://altivion.channel.io/lounge";
/** 라운지 화면 비율(가로 410 × 세로 700) */
const LOUNGE_RATIO = 410 / 700;
const HERO_PADDING_TOP = "clamp(40px,6vw,88px)";
const CHEVRON_PATH = "M6 3l5 5-5 5";
const CLOSE_PATH = "M4 4l10 10M14 4L4 14";

/** 대표전화 표기(「02-449-0120」·「+82-2-449-0120」)를 전화 걸기 주소로 — 숫자와 국가 번호 `+` 만 남긴다 */
const telHref = (tel: string) => `tel:${tel.replace(/[^\d+]/g, "")}`;

/**
 * 휴대폰·태블릿 화면(900px 이하·가로 터치 화면)의 연락처 묶음: 사진 위 덮개 대신 「채팅으로 문의하기」·대표전화 두 줄 + 운영시간.
 * 상담 창은 페이지 안에 두지 않고 버튼이 화면 전체로 연다(marketing-service-pages)
 */
const PHONE_CONTACT_STYLES = {
  area: {
    flex: "none",
    width: "100%",
    maxWidth: "480px",
    minHeight: 0,
    marginTop: "clamp(28px,7vw,40px)",
    borderRadius: 0,
    overflow: "visible",
    background: "none",
    // 사진 등장 효과(`data-reveal="img"`)의 둥근 모서리 잘라내기가 버튼 모서리·글자를 자르지 않게 — 반지름 0
    "--r-img": "0px",
    "& > img": { display: "none" },
  },
  box: {
    position: "static",
    display: "grid",
    gap: "10px",
    maxWidth: "none",
    padding: 0,
    borderRadius: 0,
    background: "none",
    boxShadow: "none",
    "@supports (backdrop-filter:blur(8px))": {
      background: "none",
      backdropFilter: "none",
    },
  },
  // 대표전화·운영시간은 상자 없이 줄글(사용자) — 전화 줄은 줄 전체를 누르면 전화를 건다
  list: {
    display: "grid",
    gap: "6px",
    "& [data-tel-row]": {
      position: "relative",
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: "12px",
      padding: "8px 0 4px",
    },
    "& dt": { fontSize: "15px", color: SITE_COLOR.ink2 },
    "& [data-tel]": { fontSize: "20px" },
    "& [data-tel] a::after": {
      content: '""',
      position: "absolute",
      inset: 0,
    },
    "& [data-hours-row]": { padding: 0 },
    "& [data-hours]": {
      fontSize: "14px",
      lineHeight: 1.7,
      color: SITE_COLOR.ink3,
    },
  },
} as const;

/**
 * 가로 터치 화면의 연락 묶음: 낮은 화면이라 버튼·줄 간격을 좁혀 묶음 높이를 왼쪽 제목 묶음에 가깝게 — 세로 가운데를 맞출 때
 * 버튼이 제목보다 한참 위에서 시작하지 않는다(2026-11-02 사용자)
 */
const LANDSCAPE_CONTACT_LIST = {
  ...PHONE_CONTACT_STYLES.list,
  gap: "4px",
  "& [data-tel-row]": {
    ...PHONE_CONTACT_STYLES.list["& [data-tel-row]"],
    padding: "4px 0 2px",
  },
  "& [data-hours]": {
    ...PHONE_CONTACT_STYLES.list["& [data-hours]"],
    lineHeight: 1.5,
  },
} as const;

const INQUIRY_STYLES = {
  hero: {
    paddingBlock: `${HERO_PADDING_TOP} clamp(72px,9.6vw,144px)`,
    [SITE_MEDIA.landscapeTouch]: { paddingBlock: "32px 56px" },
  },
  grid: {
    ...SITE_WRAP,
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) auto",
    gap: "clamp(28px,5vw,80px)",
    alignItems: "stretch",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr" },
    // 가로 터치 화면: 제목·문구 왼쪽, 연락처 묶음 오른쪽 — 한 화면에 다 들어온다
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "minmax(0,1fr) minmax(0,400px)",
      gap: "clamp(24px,4vw,48px)",
      alignItems: "center",
    },
  },
  left: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    [SITE_MEDIA.landscapeTouch]: { display: "contents" },
  },
  header: SITE_HERO_RISE_STYLES,
  title: { ...SITE_TYPE.h1, marginTop: "4px" },
  copy: {
    marginTop: "clamp(24px,3vw,44px)",
    fontSize: "clamp(24px,2.6vw,36px)",
    lineHeight: 1.38,
    fontWeight: 700,
    letterSpacing: "-.03em",
    color: SITE_COLOR.ink3,
    textWrap: "balance",
    "& em": { fontStyle: "normal", color: SITE_COLOR.blue },
  },
  /** 넓은 화면: 현장 사진 위에 연락처 덮개. 휴대폰·태블릿 화면: 사진 없이 연락처 묶음만 */
  contactArea: {
    position: "relative",
    flex: 1,
    minHeight: "240px",
    marginTop: "clamp(32px,4vw,56px)",
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    background: SITE_COLOR.panel,
    "& > img": {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    [SITE_MEDIA.tablet]: PHONE_CONTACT_STYLES.area,
    [SITE_MEDIA.landscapeTouch]: { ...PHONE_CONTACT_STYLES.area, marginTop: 0 },
  },
  contact: {
    position: "absolute",
    left: "clamp(14px,2vw,24px)",
    bottom: "clamp(14px,2vw,24px)",
    right: "clamp(14px,2vw,24px)",
    background: "rgba(255,255,255,.94)",
    borderRadius: "16px",
    padding: "18px 22px",
    maxWidth: "520px",
    boxShadow: "0 8px 28px rgba(0,0,0,.12)",
    "@supports (backdrop-filter:blur(8px))": {
      background: "rgba(255,255,255,.84)",
      backdropFilter: "saturate(180%) blur(14px)",
    },
    [SITE_MEDIA.tablet]: PHONE_CONTACT_STYLES.box,
    [SITE_MEDIA.landscapeTouch]: PHONE_CONTACT_STYLES.box,
  },
  contactList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px 32px",
    alignItems: "flex-end",
    margin: 0,
    "& dt": { fontSize: "14px", fontWeight: 600, color: SITE_COLOR.ink3 },
    "& [data-tel] a": { color: "inherit", textDecoration: "none" },
    "& [data-tel]": {
      fontSize: "clamp(24px,2.2vw,30px)",
      fontWeight: 700,
      letterSpacing: "-.02em",
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1.3,
    },
    "& [data-hours]": {
      fontSize: "14px",
      lineHeight: 1.65,
      color: SITE_COLOR.ink2,
    },
    "@media (max-width:560px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "10px",
    },
    [SITE_MEDIA.tablet]: PHONE_CONTACT_STYLES.list,
    [SITE_MEDIA.landscapeTouch]: LANDSCAPE_CONTACT_LIST,
  },
  /** 휴대폰·태블릿 화면에서만 보이는 상담 창 열기 버튼 */
  chatOpen: {
    display: "none",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    width: "100%",
    height: "60px",
    padding: "0 22px",
    borderRadius: "16px",
    background: SITE_COLOR.blue,
    color: "#fff",
    fontSize: "17px",
    fontWeight: 700,
    letterSpacing: "-.01em",
    "&:active": { transform: "scale(.99)" },
    [SITE_MEDIA.tablet]: { display: "flex" },
    [SITE_MEDIA.landscapeTouch]: {
      display: "flex",
      height: "48px",
      fontSize: "16px",
      borderRadius: "14px",
    },
  },
  // 상담 창 높이 = 첫 화면 − 머리글 − 위 여백 − 아래 안내 줄(56px): 왼쪽 사진·연락처도 같은 높이로 첫 화면에 들어온다
  chat: {
    position: "sticky",
    top: `calc(${SITE_HEADER_HEIGHT_PX}px + 24px)`,
    width: `min(410px,calc((100svh - ${SITE_HEADER_HEIGHT_PX}px - ${HERO_PADDING_TOP} - 56px) * ${truncateNumber(
      LOUNGE_RATIO,
      4
    )}))`,
    minWidth: "300px",
    alignSelf: "start",
    [`html[${SITE_MOTION_ATTR}] &`]: {
      animation: `inquiryChatIn 1s ${SITE_EASE.out} .15s both`,
    },
    "@keyframes inquiryChatIn": {
      from: { opacity: 0, transform: "translate3d(0,28px,0) scale(.98)" },
      to: { opacity: 1, transform: "none" },
    },
    // 휴대폰·태블릿 화면: 페이지 안 작은 상담 창 대신 「채팅으로 문의하기」가 화면 전체로 연다
    [SITE_MEDIA.tablet]: { display: "none" },
    [SITE_MEDIA.landscapeTouch]: { display: "none" },
  },
  card: {
    position: "relative",
    aspectRatio: "410/700",
    borderRadius: "30px",
    overflow: "hidden",
    background: "rgb(247,247,248)",
    boxShadow: "rgba(0,0,0,.3) 0 12px 60px 5px",
  },
  lounge: { height: "100%" },
  more: { paddingTop: "clamp(40px,6vw,88px)" },
  moreTitle: SITE_TYPE.h3,
  moreGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: "clamp(20px,2.6vw,36px)",
    marginTop: "clamp(32px,4vw,48px)",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "1fr", gap: "40px" },
    // 가로 터치 화면: 한 줄에 하나면 카드 그림 하나가 화면을 넘는다 — 넓은 화면처럼 세 칸
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "repeat(3,minmax(0,1fr))",
      gap: "clamp(16px,2.4vw,24px)",
    },
  },
} as const;

/** 휴대폰·태블릿 화면의 화면 전체 상담 대화상자 */
const CHAT_DIALOG_STYLES = {
  paper: { display: "flex", flexDirection: "column", background: "#fff" },
  // 대화상자는 문서 끝에 따로 붙어 사이트 뿌리 밖이다 — 사이트 글꼴·토큰·단추 초기화를 안에서 다시 건다
  site: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
  bar: {
    flex: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    minHeight: "56px",
    padding: "env(safe-area-inset-top,0px) 8px 0 20px",
    borderBottom: `1px solid ${SITE_COLOR.line}`,
  },
  title: { fontSize: "17px", fontWeight: 700, letterSpacing: "-.01em" },
  close: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "44px",
    padding: "0 12px",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: 600,
    color: SITE_COLOR.ink,
    "&:active": { background: SITE_COLOR.panel },
  },
  frame: {
    flex: 1,
    minHeight: 0,
    paddingBottom: "env(safe-area-inset-bottom,0px)",
  },
} as const;

/** 그리기 전 자리 높이(넓은 화면, 900px 이하) — 시안 `--cis`·`--cism` */
const MORE_OFFSCREEN_SIZE = [900, 1800] as const;

const MORE_CARDS = [
  { key: "manual", href: "/manual", art: "art/tile_manual.svg" },
  { key: "plan", href: "/plan", art: "art/tile_plan.svg" },
  { key: "survey", href: "/survey", art: "art/tile_survey.svg" },
] as const satisfies ReadonlyArray<{
  key: string;
  href: string;
  art: SiteFileKey;
}>;

/**
 * 1:1 문의(`/inquiry`, marketing-service-pages): 왼쪽 제목·사진·대표전화, 오른쪽 채널톡 라운지 상담 창, 아래 살펴볼 곳.
 * 휴대폰·태블릿 화면은 사진·페이지 안 상담 창 대신 「채팅으로 문의하기」·대표전화 묶음이고, 상담 창은 화면 전체 대화상자로 연다.
 */
export function InquiryPage() {
  const { t, i18n } = useTranslation("landing");
  const titleId = useId();
  const moreTitleId = useId();
  const chatTitleId = useId();
  const [chatOpen, setChatOpen] = useState(false);
  const hours = t("site.contact.hoursLines", {
    returnObjects: true,
  }) as readonly string[];
  const tel = siteTelFor(i18n.language);
  const call = { tel, href: telHref(tel) };
  return (
    <>
      <Box
        component="section"
        sx={INQUIRY_STYLES.hero}
        aria-labelledby={titleId}
      >
        <Box sx={INQUIRY_STYLES.grid}>
          <Box sx={INQUIRY_STYLES.left}>
            <Box component="header" sx={INQUIRY_STYLES.header}>
              <Box component="p" sx={SITE_TYPE.eyebrow}>
                {t("site.inquiry.eyebrow")}
              </Box>
              <Box
                component="h1"
                id={titleId}
                style={riseOrder(1)}
                sx={INQUIRY_STYLES.title}
              >
                <SiteTitle text={t("site.inquiry.title")} />
              </Box>
              <Box component="p" style={riseOrder(2)} sx={INQUIRY_STYLES.copy}>
                <SiteText text={t("site.inquiry.copy")} />
              </Box>
            </Box>
            <Box
              ref={revealRef}
              data-reveal="img"
              sx={INQUIRY_STYLES.contactArea}
            >
              <SiteImage
                name="drone_work"
                sizes="(max-width: 900px) 100vw, 760px"
                alt={t("site.inquiry.photoAlt")}
                priority
                placeholder="blur"
              />
              <Box sx={INQUIRY_STYLES.contact}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setChatOpen(true)}
                  sx={INQUIRY_STYLES.chatOpen}
                >
                  {t("site.inquiry.chatOpen")}
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path
                      d={CHEVRON_PATH}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Box>
                <Box component="dl" sx={INQUIRY_STYLES.contactList}>
                  <div data-tel-row="">
                    <dt>{t("site.contact.telLabel")}</dt>
                    <dd data-tel="">
                      <a href={call.href}>{tel}</a>
                    </dd>
                  </div>
                  <div data-hours-row="">
                    <dd data-hours="">
                      {hours.map((line, index) => (
                        <span key={line}>
                          {index > 0 && <br />}
                          {line}
                        </span>
                      ))}
                    </dd>
                  </div>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={INQUIRY_STYLES.chat}>
            <Box sx={INQUIRY_STYLES.card}>
              <LoungeFrame
                src={CHANNEL_LOUNGE_URL}
                title={t("site.inquiry.chatTitle")}
                call={call}
                sx={INQUIRY_STYLES.lounge}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        fullScreen
        aria-labelledby={chatTitleId}
        slotProps={{ paper: { sx: CHAT_DIALOG_STYLES.paper } }}
      >
        <SiteRoot sx={CHAT_DIALOG_STYLES.site}>
          <Box sx={CHAT_DIALOG_STYLES.bar}>
            <Box component="h2" id={chatTitleId} sx={CHAT_DIALOG_STYLES.title}>
              {t("site.inquiry.chatTitle")}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => setChatOpen(false)}
              sx={CHAT_DIALOG_STYLES.close}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                aria-hidden="true"
              >
                <path
                  d={CLOSE_PATH}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              {t("site.inquiry.chatClose")}
            </Box>
          </Box>
          <LoungeFrame
            src={CHANNEL_LOUNGE_URL}
            title={t("site.inquiry.chatTitle")}
            call={call}
            sx={CHAT_DIALOG_STYLES.frame}
          />
        </SiteRoot>
      </Dialog>
      <SiteSection
        labelledBy={moreTitleId}
        skipOffscreen={MORE_OFFSCREEN_SIZE}
        sx={INQUIRY_STYLES.more}
      >
        <Box sx={SITE_WRAP}>
          <Box
            component="h2"
            id={moreTitleId}
            ref={revealRef}
            data-reveal="up"
            sx={INQUIRY_STYLES.moreTitle}
          >
            <SiteTitle text={t("site.inquiry.moreTitle")} />
          </Box>
          <Box sx={INQUIRY_STYLES.moreGrid}>
            {MORE_CARDS.map((card, index) => (
              <LinkCard
                key={card.key}
                href={card.href}
                revealIndex={index}
                media={
                  <SiteFileImage
                    file={card.art}
                    width={600}
                    height={400}
                    alt={t(`site.inquiry.${card.key}.alt`)}
                  />
                }
                kicker={t(`site.inquiry.${card.key}.kicker`)}
                title={t(`site.inquiry.${card.key}.title`)}
                text={t(`site.inquiry.${card.key}.text`)}
              />
            ))}
          </Box>
        </Box>
      </SiteSection>
    </>
  );
}
